import { useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

interface Props {
  code: string
  petName: string
}

type State = 'prompt' | 'form' | 'submitting' | 'done'

export default function FoundPetReport({ code, petName }: Props) {
  const [state, setState] = useState<State>('prompt')
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)
  const [address, setAddress] = useState('')
  const [finderName, setFinderName] = useState('')
  const [finderPhone, setFinderPhone] = useState('')
  const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'done' | 'manual'>('idle')

  const shareLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('manual')
      setState('form')
      return
    }

    setLocationStatus('loading')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude)
        setLongitude(pos.coords.longitude)
        setLocationStatus('done')
        setState('form')
      },
      () => {
        setLocationStatus('manual')
        setState('form')
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const submit = async () => {
    setState('submitting')
    try {
      await fetch(`${API_URL}/api/tags/${code}/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latitude,
          longitude,
          address: address || null,
          finderName: finderName || null,
          finderPhone: finderPhone || null,
        }),
      })
      setState('done')
    } catch {
      setState('form')
    }
  }

  const canSubmit = latitude != null || address || finderName || finderPhone

  if (state === 'done') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">&#10003;</span>
          <div>
            <p className="font-semibold text-green-800">Report Sent!</p>
            <p className="text-green-700 text-sm">The owner has been notified</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card mt-4">
      <h3 className="font-bold text-gray-900 mb-2">Help Reunite {petName}</h3>
      <p className="text-sm text-gray-500 mb-4">
        Share your location so the owner can find their pet faster
      </p>

      {state === 'prompt' && (
        <div className="flex gap-3">
          <button
            onClick={shareLocation}
            disabled={locationStatus === 'loading'}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {locationStatus === 'loading' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <span>&#128205;</span>
            )}
            Share Location
          </button>
          <button
            onClick={() => { setLocationStatus('manual'); setState('form') }}
            className="btn-secondary"
          >
            Enter Address
          </button>
        </div>
      )}

      {(state === 'form' || state === 'submitting') && (
        <div className="space-y-3">
          {locationStatus === 'done' && (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 p-2 rounded">
              <span>&#128205;</span>
              <span className="text-sm">Location shared</span>
            </div>
          )}

          {locationStatus === 'manual' && (
            <input
              type="text"
              placeholder="Enter address or cross streets"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="input-field"
            />
          )}

          <p className="text-xs text-gray-500 mt-2">Your contact info (optional)</p>
          <input
            type="text"
            placeholder="Your name"
            value={finderName}
            onChange={(e) => setFinderName(e.target.value)}
            className="input-field"
          />
          <input
            type="tel"
            placeholder="Your phone"
            value={finderPhone}
            onChange={(e) => setFinderPhone(e.target.value)}
            className="input-field"
          />

          <button
            onClick={submit}
            disabled={!canSubmit || state === 'submitting'}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {state === 'submitting' && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            {state === 'submitting' ? 'Sending...' : 'Send Report to Owner'}
          </button>
        </div>
      )}
    </div>
  )
}
